import { useWeb3React } from '@web3-react/core'
import { useCallback } from 'react'
import { useAppDispatch } from 'store'
import { useLinkConnected, useLinkSigner } from 'store/link/hooks'
import { updateModal } from '../store/app/actions/modalActions'
import { useConnectLinkWallet } from './link/useConnectLinkWallet'
import { useAccount } from '@starknet-react/core'
import { useChainType } from '../store/connect/hooks'
import { ChainType } from '../constants/chains'
import toastify from '../components/Toastify'
import { connectZkexAction } from '../store/account/actions/connectZkexActions'
import { isFulfilled } from '@reduxjs/toolkit'
import { UserRejectedError } from 'utils/errors/userReject'

export function useConnectZkex() {
  const dispatch = useAppDispatch()
  const { provider } = useWeb3React()
  const { account } = useAccount()
  const chainType = useChainType()
  const signer = useLinkSigner()
  const isLinkConnected = useLinkConnected()
  const connectToLink = useConnectLinkWallet()
  return useCallback(async () => {
    const web3Provider = chainType === ChainType.Starknet ? account : provider

    // If the connector is not ready, close the verify modal.
    // User needs to click the 'Connect Wallet' button again,
    // and then will see the web3 selection modal
    if (!web3Provider) {
      dispatch(updateModal({ modal: 'verify', open: false }))
      toastify.error('Connector is not ready, please try again later')
      return
    }

    let _signer = signer
    if (!_signer || !isLinkConnected) {
      // Try to connect to Link Wallet
      _signer = await connectToLink(web3Provider).catch((e) => {
        // If user rejected the connect link wallet, do nothing
        if (UserRejectedError.is(e)) {
          console.debug('User rejected connect link wallet')
        }
        // We need to show the error message to the user
        // Because the connect action is not user rejected
        else {
          toastify.error(e.message)
        }
        return undefined
      })
    }

    // If the signer is not ready, stop here
    if (!_signer) return

    const actionResult = await dispatch(
      connectZkexAction({ provider: web3Provider, signer: _signer }),
    )

    // Try to close the Link Wallet modal after the user has logged in
    if (isFulfilled(actionResult))
      dispatch(updateModal({ modal: 'verify', open: false }))
  }, [
    dispatch,
    provider,
    account,
    chainType,
    signer,
    isLinkConnected,
    connectToLink,
  ])
}
